import type { OpeningHours, Slot } from 'app/components/commons/Hotel/Types';
import type { DateRange } from 'app/redux/models/Experience/Experience';
import { OpeningHoursType } from 'app/typings/core/admin/experiences/openingHours/openingHours';
import { dayjs, format } from 'app/utils/dayjs';
import type { Dayjs } from 'app/utils/dayjs';

export const DAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

type FormSlot = [Dayjs, Dayjs];

type DayFormState = {
  enabled: boolean;
  isAllDay: boolean;
  slots: FormSlot[];
};

export type FormOpeningHours = {
  type: string;
  innerType?: string;
  isAllDay: boolean;
  slots: FormSlot[];
  days: Record<string, DayFormState>;
};

export const originalToFormSlot = (slot: Slot): FormSlot => {
  // Most specific format FIRST: dayjs' customParseFormat picks the first format
  // that parses, and it leniently accepts 'HH:mm:ss' even for a full datetime
  // string (yielding the wrong time). moment picked the best match regardless
  // of order; dayjs does not. dayjs.utc()'s types only allow a single format
  // string, so we parse with base dayjs (which accepts the array) then flag as
  // UTC keeping the wall clock — equivalent to moment.utc(value, formats).
  const formats = ['YYYY-MM-DDTHH:mm:ss', 'HH:mm:ss'];
  const start = dayjs(slot.start ?? undefined, formats).utc(true);
  const end = dayjs(slot.end ?? undefined, formats).utc(true);

  return [start, end];
};

export const parseOpeningHoursFromAPI = (
  openingHours: OpeningHours | undefined | null,
  nonApplicableOption = false
): FormOpeningHours => {
  const defaultSlot: Slot[] = [{ start: '09:00:00', end: '18:00:00' }];

  if (!openingHours) {
    return {
      type: nonApplicableOption ? 'na' : 'weekly',
      innerType: nonApplicableOption ? 'na' : 'weekly',
      isAllDay: false,
      slots: defaultSlot.map(originalToFormSlot),
      days: DAYS.reduce<Record<string, DayFormState>>((acc, key) => {
        acc[key] = {
          enabled: false,
          isAllDay: false,
          slots: defaultSlot.map(originalToFormSlot),
        };

        return acc;
      }, {}),
    };
  }

  const slots = openingHours.slots || defaultSlot;

  return {
    ...openingHours,
    type: openingHours.type || 'weekly',
    innerType: openingHours.type || 'weekly',
    isAllDay: !!openingHours.isAllDay,
    slots: slots.map(originalToFormSlot),
    days: DAYS.reduce<Record<string, DayFormState>>((acc, key) => {
      const dailySlots = openingHours.days?.[key]?.slots || defaultSlot;

      acc[key] = {
        ...openingHours.days?.[key],
        enabled: !!openingHours.days?.[key],
        isAllDay: !!openingHours.days?.[key]?.isAllDay,
        slots: dailySlots.map(originalToFormSlot),
      };

      return acc;
    }, {}),
  };
};

export const formatOpeningHoursForAPI = (
  openingHours: FormOpeningHours
): OpeningHours | null => {
  if (openingHours.type === 'na') {
    return null;
  }

  if (openingHours.type === OpeningHoursType.WEEKLY) {
    if (openingHours.isAllDay) {
      return {
        type: OpeningHoursType.WEEKLY,
        isAllDay: true,
      };
    }

    return {
      type: OpeningHoursType.WEEKLY,
      slots: openingHours.slots.map((formattedSlot) => ({
        start: formattedSlot?.[0] && dayjs(formattedSlot[0]).format('HH:mm:ss'),
        end: formattedSlot?.[1] && dayjs(formattedSlot[1]).format('HH:mm:ss'),
      })),
    };
  }

  return {
    type: OpeningHoursType.DAILY,
    days: DAYS.reduce<Record<string, { isAllDay?: boolean; slots?: Slot[] }>>(
      (acc, key) => {
        const dayHours = openingHours.days[key];

        if (!dayHours.enabled) {
          return acc;
        }

        if (dayHours.isAllDay) {
          acc[key] = {
            isAllDay: true,
          };
        } else {
          acc[key] = {
            slots: dayHours.slots.map((formattedSlot) => ({
              start:
                formattedSlot?.[0] &&
                dayjs(formattedSlot[0]).format('HH:mm:ss'),
              end:
                formattedSlot?.[1] &&
                dayjs(formattedSlot[1]).format('HH:mm:ss'),
            })),
          };
        }

        return acc;
      },
      {}
    ),
  };
};

export const parseDateRangeFromAPI = (
  dateRanges: DateRange[]
): [Date, Date][] => {
  return dateRanges.map((d) => [d.start, d.end]);
};

export const formatDateRangeForAPI = (
  dateRanges?: [Date, Date][]
): { start: string; end: string }[] => {
  if (!dateRanges) {
    return [];
  }

  return dateRanges.map((arr) => {
    const [start, end] = arr;

    return {
      start: format(start, 'YYYY-MM-DD'),
      end: format(end, 'YYYY-MM-DD'),
    };
  });
};
